import { useEffect, useState } from 'react';
import { api, darf, datum, heuteIso, tagIso, uhr, type Anmelder }
  from '../api.ts';
import {
  Dialog, Fehlerkasten, Feldreihe, Text, ausFehler, type Fehlerlage,
} from './Formular.tsx';
import Bruchtext from './Bruchtext.tsx';
import { Dateikachel, type Kacheldatei } from './Dateikachel.tsx';
import { useDateifenster } from './Dateifenster.tsx';
import { Klammer } from './Klammer.tsx';
import { useFrisch } from './Frisch.tsx';
import { fragen } from './rueckfrage.tsx';

/**
 * DAS MAILFACH: DAS POSTFACH SELBST, WIE IN OUTLOOK.
 * ===========================================================================
 *
 *   ORIGINALTEXT MARVIN (08.09.2026)
 *     „ich will nun imap einhängen, er soll die ordnerstruktur lesen und wwie
 *      gesagt es soll ein wneig wie outlook sein, dass man auch daraus mails
 *      versenden kann, zugriff auf die textvorlagen hat, zb für AAZ/VAO/FAZ
 *      ... ich mails da raus ziehen kann um an den auftrag zu hängen ...
 *      gleichzeitig, wenn ich dann emails löshe oder verschoben werden durch
 *      regeln in ordner soll es im imap auch passieren."
 *
 * ES IST DIESELBE DREISPALTIGE GESTALT WIE NEBENAN, und das ist Absicht: wer
 * den Reiter wechselt, wechselt nicht die Bedienung. Was sich unterscheidet,
 * ist die QUELLE - links stehen hier die Ordner des Mailservers und nicht die
 * drei Zustände der Ausnahmenliste.
 *
 * UND ES IST KEINE ABSCHRIFT. Jeder Klick geht an den Mailserver; was hier
 * verschoben oder gelöscht wird, ist auch in Outlook verschoben oder
 * gelöscht. Deshalb steht neben jedem Fehler der GRUND: ein Postfach, das
 * nicht antwortet, sieht sonst aus wie ein leeres.
 *
 * ---------------------------------------------------------------------------
 * DIE DREI FENSTER DARIN SIND FENSTER DIESES HAUSES UND KEINE EIGENEN.
 *
 *   ORIGINALTEXT MARVIN (08.09.2026, Punkt 7)
 *     „Postfach: Design, Thumbnails der Anhänge, Anhänge in einem Fenster
 *      statt neuem Tab, Büroklammer statt Mailsymbol am Auftrag, verschobene
 *      Popups"
 *
 * SIE HABEN `.dialoggrund` UND `.dialog` SELBST GEBAUT - als einzige drei im
 * Haus. Was ihnen dabei fehlte, ist nicht Zierde, sondern jedes Mal etwas,
 * das `Dialog` (in `Formular.tsx`) mitbringt:
 *
 *   die BREITENDECKELUNG    `width: min(Xpx, 96vw)`. Ohne sie hat ein Fenster
 *                           gar keine Breite und wächst mit seinem Inhalt -
 *                           bei einer langen Trefferliste über den Rand
 *                           hinaus, und `.dialoggrund { overflow: hidden }`
 *                           schneidet dort ab. Genau das sind die
 *                           „verschobenen Popups".
 *   der ROLLENDE LEIB       `.dialogleib` trägt `overflow-y: auto`,
 *                           `min-height: 0` und `flex: 1 1 auto`; `.leib` ist
 *                           die Klasse einer KARTE und trägt davon nichts.
 *                           Ohne sie wächst der Kasten über `max-height: 96vh`
 *                           hinaus, statt innen zu rollen.
 *   der KOPF, DER BLEIBT    `.dialogkopf` ist `flex: none`. `.kopf` rollt mit.
 *   ESCAPE UND FENSTERSTAPEL  nur das OBERSTE Fenster hört auf Escape
 *                           (`fensterstapel.ts`).
 *   DER FOKUS IN DEN LEIB   und nicht auf „schließen" in der Kopfzeile.
 *
 * UND `className="dialog breit"` HAT NIE ETWAS GETAN: eine Regel `.dialog.breit`
 * gibt es in `stil.css` nicht. **Eine Klasse, die es nicht gibt, ist ein Wort
 * ohne Wirkung, und CSS meldet dazu nichts** - dieselbe Falle wie
 * `.knopf.warnung` (07.09.2026) und `var(--laeuft)` (31.08.2026). Die beiden
 * Fenster, die sich für breit hielten, waren so breit wie ihr Inhalt.
 *
 * ZWEI FASSUNGEN DESSELBEN FENSTERS LAUFEN BEIM ERSTEN NACHTRAG AUSEINANDER.
 * Der Fensterstapel ist am 06.09.2026 dazugekommen, die Breitendeckelung
 * früher; diese drei haben beides nie mitbekommen, weil sie danebenstanden.
 */

/** Der Weg zurück in die Liste, auf dem Telefon. */
function Zurueck({ tu }: { tu: () => void }) {
  return (
    <button type="button" className="knopf nurschmal" onClick={tu}>
      ← Liste
    </button>
  );
}

function wann(w: string | null): string {
  if (!w) return '';
  const d = new Date(w);
  if (Number.isNaN(d.getTime())) return '';
  /* „IST DAS HEUTE" IN DEUTSCHER ZEIT. Hier standen die lokalen Getter,
     also die Uhr des Betrachters, und die Uhrzeit daneben trug keine
     Zeitzone - waehrend `uhr()` sie seit jeher traegt. Wer aus einer
     anderen Zone hineinsah, bekam „heute" fuer eine Mail von gestern und
     eine Uhrzeit, die zwei Stunden danebenlag. */
  const gleich = tagIso(d) === heuteIso();
  return gleich ? uhr(d) : datum(d);
}

function kurzWer(u: any): string {
  const v = (u?.von ?? [])[0];
  if (!v) return 'ohne Absender';
  return v.name || String(v.adresse ?? '').split('@')[0];
}

export default function Mailfach(
  { ich, oeffne, verwaltenAuf }:
  { ich: Anmelder; oeffne: (auftragId: string) => void; verwaltenAuf?: boolean }
) {
  const [alleKonten, setKonten] = useState<any[] | null>(null);
  /* NUR FÜR ABLÄUFE (.489): solche Postfächer liest nur die Plattform; zum Blättern stehen sie nicht da. */
  const konten = alleKonten ? alleKonten.filter((k: any) => !k.nur_ablaeufe) : null;
  const [konto, setKonto] = useState<string>('');
  const [verwalten, setVerwalten] = useState(!!verwaltenAuf);
  const [ordner, setOrdner] = useState<any[] | null>(null);
  const [gesendet, setGesendet] = useState<string | null>(null);
  const [wo, setWo] = useState<string>('');
  const [suche, setSuche] = useState('');
  const [liste, setListe] = useState<any>(null);
  const [offen, setOffen] = useState<number | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [schreiben, setSchreiben] = useState<any>(null);

  const darfKonten = darf(ich, 'postfach.konten');
  const darfSortieren = darf(ich, 'postfach.zuordnen');
  const darfSenden = darf(ich, 'nachricht.senden');

  async function kontenLaden() {
    try {
      const r = await api.mailkonten();
      setKonten(r.konten ?? []);
      const erstes = (r.konten ?? []).find((k: any) => k.aktiv && !k.nur_ablaeufe);
      if (erstes && !konto) setKonto(erstes.id);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { kontenLaden(); }, []);

  async function ordnerLaden() {
    if (!konto) { setOrdner(null); return; }
    setOrdner(null); setListe(null); setOffen(null);
    try {
      const r = await api.mailfachOrdner(konto);
      setOrdner(r.ordner ?? []);
      setGesendet(r.gesendet ?? null);
      const start = (r.ordner ?? []).find((o: any) => o.rolle === 'posteingang')
                 ?? (r.ordner ?? []).find((o: any) => !o.nichtWaehlbar);
      setWo(start?.roh ?? '');
      setFehler(null);
    } catch (f) { setFehler(ausFehler(f)); setOrdner([]); }
  }
  useEffect(() => { ordnerLaden(); }, [konto]);

  async function listeLaden(seite = 0) {
    if (!konto || !wo) return;
    setListe(null); setOffen(null);
    try {
      setListe(await api.mailfachMails(konto, wo, seite, suche));
      setFehler(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { listeLaden(0); }, [wo]);

  /* KEIN POSTFACH EINGETRAGEN IST KEIN FEHLER, SONDERN EIN NÄCHSTER SCHRITT.
     Eine leere Fläche wäre von einem Ausfall nicht zu unterscheiden. */
  if (konten && konten.length === 0) {
    return (
      <div className="karte">
        <h3>Noch ist kein Postfach eingehängt</h3>
        <p className="klein">
          Hier steht das Postfach selbst - Ordner, Mails, Antworten -, so wie
          Sie es in Outlook sehen. Der automatische Weg über n8n läuft davon
          unberührt weiter.
        </p>
        {darfKonten
          ? <button className="knopf haupt"
                    onClick={() => setVerwalten(true)}>Postfach eintragen</button>
          : <p className="klein leise">
              Eintragen darf es, wer die Konfiguration pflegt.
            </p>}
        {verwalten && (
          <Postfaecher ich={ich} zu={() => { setVerwalten(false); kontenLaden(); }} />
        )}
      </div>
    );
  }

  const zeilen: any[] = liste?.zeilen ?? [];

  return (
    <>
      <div className="reihe" style={{ marginBottom: 10 }}>
        {(konten ?? []).length > 1 && (
          <select value={konto} onChange={(e) => setKonto(e.target.value)}>
            {(konten ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}{k.aktiv ? '' : ' (stillgelegt)'}
              </option>
            ))}
          </select>
        )}
        <input type="search" autoComplete="off" value={suche}
               placeholder="Betreff oder Absender"
               onChange={(e) => setSuche(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') listeLaden(0); }} />
        <button className="knopf" onClick={() => listeLaden(0)}>Suchen</button>
        {suche && (
          <button className="knopf"
                  onClick={() => { setSuche(''); setTimeout(() => listeLaden(0), 0); }}>
            Suche zurücksetzen
          </button>
        )}
        <span className="wachsen" />
        {darfSenden && konto && (
          <button className="knopf haupt"
                  onClick={() => setSchreiben({ an: '', betreff: '', text: '' })}>
            Neue Mail
          </button>
        )}
        {darfKonten && (
          <button className="knopf" onClick={() => setVerwalten(true)}>
            Postfächer verwalten
          </button>
        )}
      </div>

      <Fehlerkasten lage={fehler} />

      <div className={'mailprogramm' + (offen !== null ? ' liest' : '')}>
        <nav className="mailordner">
          {!ordner && !fehler && <p className="klein leise">Wird geladen.</p>}
          {(ordner ?? []).filter((o) => !o.nichtWaehlbar).map((o) => (
            <button key={o.roh} type="button"
                    className={'ordnerknopf' + (wo === o.roh ? ' aktiv' : '')}
                    title={o.name}
                    onClick={() => setWo(o.roh)}>
              <span className="zeichen" aria-hidden="true">
                {o.rolle === 'posteingang' ? '↓'
                  : o.rolle === 'gesendet' ? '↑'
                  : o.rolle === 'papierkorb' ? '×'
                  : o.rolle === 'entwuerfe' ? '~' : '·'}
              </span>
              <span className="wachsen"
                    style={{ paddingLeft: o.ebene * 10 }}>{o.kurz}</span>
              {wo === o.roh && liste ? <span className="zahl">{liste.gesamt}</span>
                                     : null}
            </button>
          ))}
          {ordner && ordner.length === 0 && !fehler && (
            <p className="klein">Dieses Postfach hat keine Ordner.</p>
          )}
        </nav>

        <div className="mailliste">
          {!liste && !fehler && <p className="klein leise">Wird geladen.</p>}
          {liste && zeilen.length === 0 && (
            <p className="klein" style={{ padding: '10px 12px' }}>
              {suche ? 'Kein Treffer in diesem Ordner.' : 'Dieser Ordner ist leer.'}
            </p>
          )}
          {zeilen.map((z) => (
            <button key={z.uid} type="button"
                    className={'mailzeile' + (offen === z.uid ? ' aktiv' : '')
                               + (z.gelesen ? '' : ' ungelesen')}
                    onClick={() => setOffen(z.uid)}>
              <span className="oben">
                {z.am_auftrag && (
                  <span className="merker klein gruen">am Auftrag</span>
                )}
                <b className="wachsen">{kurzWer(z.umschlag)}</b>
                <span className="klein leise nichtumbrechen">
                  {wann(z.datum)}
                </span>
              </span>
              <span className="betreff">
                {z.umschlag?.betreff
                  || <span className="leise">ohne Betreff</span>}
              </span>
              <span className="unten klein leise">
                {(z.umschlag?.von ?? [])[0]?.adresse ?? ''}
                {/* EINE KLAMMER STATT DES WORTES. Sie steht nur an den
                    Zeilen, an denen wirklich etwas hängt - in einer Liste,
                    in der jede Zeile eine Mail ist, sagt ein Mailsymbol
                    nichts. */}
                {z.hatAnhang && <>, <Klammer titel="Anhang" /></>}
              </span>
            </button>
          ))}
          {/* EINE WERKZEUGLEISTE IST EINE ZEILE UND KEIN RASTER.
              `knopfleiste` legt `repeat(auto-fill, minmax(104px, 1fr))` an -
              richtig in einer Tabellenzelle, wo Zeile für Zeile dieselben
              Knöpfe untereinander stehen sollen, falsch bei verschieden langen
              Aufschriften nebeneinander: dann bekommt „Neuere" die Breite von
              „Ältere" und die Spalten daneben bleiben leer. Prüfstand 041 (1q)
              misst die Abwesenheit des Rasters ausserhalb einer Zelle. */}
          {liste && liste.gesamt > zeilen.length && (
            <div className="reihe" style={{ padding: 8 }}>
              <button className="knopf" disabled={liste.seite === 0}
                      onClick={() => listeLaden(liste.seite - 1)}>Neuere</button>
              <button className="knopf"
                      onClick={() => listeLaden(liste.seite + 1)}>Ältere</button>
              <span className="klein leise">
                {liste.gesamt} im Ordner
              </span>
            </div>
          )}
        </div>

        <div className="maillese">
          {offen !== null && konto ? (
            <Mail kontoId={konto} ordner={wo} uid={offen}
                  ordnerliste={ordner ?? []}
                  darfSortieren={darfSortieren} darfSenden={darfSenden}
                  zurueck={() => setOffen(null)}
                  fertig={() => listeLaden(liste?.seite ?? 0)}
                  antworten={(v: any) => setSchreiben(v)}
                  oeffne={oeffne} />
          ) : (
            <div className="leerelese klein leise">
              Links eine Mail anklicken. Sie bleibt hier stehen, während Sie
              durch die Liste gehen.
            </div>
          )}
        </div>
      </div>

      {verwalten && (
        <Postfaecher ich={ich}
                     zu={() => { setVerwalten(false); kontenLaden(); }} />
      )}
      {schreiben && konto && (
        <Schreiben kontoId={konto} vorbelegt={schreiben}
                   gesendetOrdner={gesendet}
                   zu={() => setSchreiben(null)} />
      )}
    </>
  );
}

/* ========================================================================= *
 * Eine Mail
 * ========================================================================= */

function Mail(
  { kontoId, ordner, uid, ordnerliste, darfSortieren, darfSenden,
    zurueck, fertig, antworten, oeffne }: {
    kontoId: string; ordner: string; uid: number; ordnerliste: any[];
    darfSortieren: boolean; darfSenden: boolean;
    zurueck: () => void; fertig: () => void;
    antworten: (v: any) => void; oeffne: (auftragId: string) => void;
  }
) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [zuordnen, setZuordnen] = useState(false);
  /* DER HAKEN STEHT AM ANFANG DES BAUTEILS. Weiter unten steht eine bedingte
     Rückgabe („Wird geladen."), und ein Haken dahinter ist auf dem Telefon
     ein weisser Bildschirm (React-Fehler 310). */
  const { fenster: dateifenster, zeige: zeigeDatei } = useDateifenster();

  async function laden() {
    setD(null);
    try { setD(await api.mailfachMail(kontoId, ordner, uid)); setFehler(null); }
    catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); setMeldung(null); }, [kontoId, ordner, uid]);
  useFrisch(laden);

  async function tu(was: () => Promise<any>, satz?: string) {
    setLaeuft(true); setFehler(null);
    try {
      const r = await was();
      if (satz) setMeldung(satz);
      fertig();
      return r;
    } catch (f) { setFehler(ausFehler(f)); return null; }
    finally { setLaeuft(false); }
  }

  if (!d) {
    return (
      <div className="leerelese klein leise">
        <Fehlerkasten lage={fehler} />
        {!fehler && 'Wird geladen.'}
      </div>
    );
  }

  const m = d.mail;
  const u = m.umschlag ?? {};

  /* WAS DIE KACHEL ÜBER EINEN ANHANG WISSEN MUSS, STEHT EINMAL DA. Dieselbe
     Liste zeichnet die Kacheln UND füllt das Fenster zum Blättern; zwei
     Fassungen davon zeigten im Fenster eine andere Datei als auf der Kachel,
     die es geöffnet hat. */
  const anhaenge: Kacheldatei[] = (m.anhaenge ?? []).map((t: any) => ({
    id: `teil-${t.nummer}`,
    dateiname: t.dateiname ?? `Teil ${t.nummer}`,
    mime: t.art ?? null,
    groesse: t.groesse ?? null,
    adresse: api.mailfachAnhangWeg(kontoId, ordner, uid, t.nummer),
  }));

  return (
    <div className="mailvorgang">
      <div className="reihe" style={{ marginBottom: 8 }}>
        <Zurueck tu={zurueck} />
        <span className="wachsen" />
        {d.am_auftrag && (
          <button className="knopf" onClick={() => oeffne(d.am_auftrag)}>
            Zum Auftrag
          </button>
        )}
      </div>

      <h3 style={{ marginTop: 0 }}>
        <Bruchtext text={u.betreff || 'ohne Betreff'} />
      </h3>
      <p className="klein leise" style={{ marginTop: 0 }}>
        {(u.von ?? []).map((v: any) => v.name
          ? `${v.name} <${v.adresse}>` : v.adresse).join(', ')}
        {', '}{wann(m.datum)}
        {(u.an ?? []).length
          ? <>, an <Bruchtext text={(u.an ?? []).map((v: any) => v.adresse)
              .join(', ')} /></> : null}
      </p>

      <Fehlerkasten lage={fehler} />
      {meldung && <p className="hinweis klein">{meldung}</p>}

      <div className="reihe" style={{ marginBottom: 10 }}>
        {darfSenden && (
          <button className="knopf" onClick={() => antworten({
            an: (u.von ?? [])[0]?.adresse ?? '',
            betreff: /^(re|aw):/i.test(u.betreff ?? '')
              ? u.betreff : `Re: ${u.betreff ?? ''}`,
            text: '\n\n---\n' + (m.text ?? '').split('\n')
              .map((z: string) => '> ' + z).join('\n'),
            in_reply_to: u.messageId ?? null,
          })}>Antworten</button>
        )}
        {darfSortieren && (
          <button className="knopf haupt" disabled={laeuft}
                  onClick={() => setZuordnen(true)}>
            An einen Auftrag
          </button>
        )}
        {darfSortieren && (
          <VerschiebenKnopf ordnerliste={ordnerliste} jetzt={ordner}
                            tu={(ziel) => tu(
                              () => api.mailfachVerschieben(kontoId,
                                { ordner, uid, ziel }),
                              'Im Postfach verschoben.')} />
        )}
        {darfSortieren && (
          <button className="knopf warnung" disabled={laeuft}
                  onClick={() => tu(
                    () => api.mailfachLoeschen(kontoId, { ordner, uid }),
                    'In den Papierkorb des Postfachs gelegt.')}>
            Löschen
          </button>
        )}
      </div>

      {/* WAS DAS LÖSCHEN WIRKLICH TUT, STEHT DANEBEN UND NICHT IM HANDBUCH. */}
      <p className="klein leise" style={{ marginTop: 0 }}>
        Verschieben und Löschen wirken im Postfach selbst - also auch in
        Outlook. „An einen Auftrag" schreibt die Mail in die Akte und lässt
        sie hier stehen.
      </p>

      {/* DIE ANHÄNGE ALS KACHELN, UND IN EINEM FENSTER (Punkt 7).
          ORIGINALTEXT MARVIN (08.09.2026)
            „Thumbnails der Anhänge, Anhänge in einem Fenster statt neuem Tab"

          DIESELBE KACHEL WIE ÜBERALL, obwohl die Datei hier noch NICHT
          abgelegt ist: sie liegt im Postfach und kommt über
          `mailfachAnhangWeg`. Die Adresse bringt deshalb der Aufrufer mit -
          **zwei Kacheln zu bauen, weil die Adresse aus zwei Töpfen kommt,
          wäre die zweite Fassung, die beim ersten Nachtrag weniger kann.** */}
      {anhaenge.length > 0 && (
        <div className="karte">
          <h3>
            <Klammer />{' '}
            {anhaenge.length === 1 ? '1 Anhang'
                                   : `${anhaenge.length} Anhänge`}
          </h3>
          <div className="leib">
            <div className="dateigitter">
              {anhaenge.map((k) => (
                <Dateikachel key={k.id} datei={k}
                             oeffne={() => zeigeDatei(k, anhaenge)} />
              ))}
            </div>
            <p className="klein leise" style={{ marginBottom: 0 }}>
              Sie werden erst dann abgelegt, wenn die Mail an einen Auftrag
              geht.
            </p>
          </div>
        </div>
      )}

      {/* DAS FENSTER GEHÖRT DER MAIL UND NICHT DER SEITE. Wer einen Anhang
          aufmacht, blättert durch die Anhänge DIESER Mail. */}
      {dateifenster}

      <pre className="mailtext">{m.text || '(kein Text)'}</pre>

      {zuordnen && (
        <ZuordnenFenster
          zu={() => setZuordnen(false)}
          nimm={async (auftragId) => {
            const r = await tu(
              () => api.mailfachAnAuftrag(kontoId,
                { ordner, uid, auftrag_id: auftragId }),
              'An den Auftrag gehängt.');
            setZuordnen(false);
            if (r) laden();
          }} />
      )}
    </div>
  );
}

/** Verschieben: erst der Ordner, dann der Handgriff. */
function VerschiebenKnopf(
  { ordnerliste, jetzt, tu }:
  { ordnerliste: any[]; jetzt: string; tu: (ziel: string) => void }
) {
  const [ziel, setZiel] = useState('');
  const moeglich = ordnerliste.filter((o) => !o.nichtWaehlbar && o.roh !== jetzt);
  if (!moeglich.length) return null;
  return (
    <span className="reihe">
      <select value={ziel} onChange={(e) => setZiel(e.target.value)}>
        <option value="">verschieben nach …</option>
        {moeglich.map((o) => (
          <option key={o.roh} value={o.roh}>{o.name}</option>
        ))}
      </select>
      <button className="knopf" disabled={!ziel} onClick={() => tu(ziel)}>
        Verschieben
      </button>
    </span>
  );
}

/* ========================================================================= *
 * Den Auftrag suchen
 * ========================================================================= */

function ZuordnenFenster(
  { zu, nimm }: { zu: () => void; nimm: (auftragId: string | null) => void }
) {
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<any[]>([]);
  const [gesucht, setGesucht] = useState(false);

  async function suchen() {
    const p = new URLSearchParams({ suche, proSeite: '20' });
    const r = await api.auftraege(p).catch(() => ({ zeilen: [] } as any));
    setTreffer(r.zeilen ?? []); setGesucht(true);
  }

  return (
    <Dialog titel="Die Mail an einen Auftrag hängen" breite={640} zu={zu}>
      <p className="klein leise">
        Die Mail steht danach mit Text, Faden und Anhängen in der Akte -
        und bleibt im Postfach stehen.
      </p>
      <div className="reihe">
        <input value={suche} autoComplete="off"
               placeholder="Anschrift, KLS-, SM- oder BA/FA-Nummer"
               onChange={(e) => setSuche(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') suchen(); }} />
        <button className="knopf" onClick={suchen}>Suchen</button>
      </div>
      {gesucht && treffer.length === 0 && (
        <p className="klein">Kein Auftrag gefunden.</p>
      )}
      <ul className="liste">
        {treffer.map((a) => (
          <li key={a.id}>
            <button className="knopf" onClick={() => nimm(a.id)}>
              {a.strasse} {a.hausnummer}{a.hausnummer_zusatz ?? ''},
              {' '}{a.plz} {a.ort_text}
              {a.kls_id ? `, KLS ${a.kls_id}` : ''}
            </button>
          </li>
        ))}
      </ul>
      <p className="klein leise">
        Ohne Auswahl sucht das System den Auftrag selbst - über den Faden,
        die Anhänge und die Anschrift im Text.
      </p>
      <button className="knopf" onClick={() => nimm(null)}>
        Ohne Auswahl übernehmen
      </button>
    </Dialog>
  );
}

/* ========================================================================= *
 * Schreiben - mit den Vorlagen, die es schon gibt
 * ========================================================================= */

function Schreiben(
  { kontoId, vorbelegt, gesendetOrdner, zu }:
  { kontoId: string; vorbelegt: any; gesendetOrdner: string | null;
    zu: () => void }
) {
  const [e, setE] = useState<any>({
    an: vorbelegt.an ?? '', betreff: vorbelegt.betreff ?? '',
    text: vorbelegt.text ?? '',
  });
  const [vorlagen, setVorlagen] = useState<any[] | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [fertig, setFertig] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    api.mailfachVorlagen().then((r) => setVorlagen(r.vorlagen ?? []))
      .catch(() => setVorlagen([]));
  }, []);

  async function senden() {
    setLaeuft(true); setFehler(null);
    try {
      setFertig(await api.mailfachSenden(kontoId, {
        an: e.an, betreff: e.betreff, text: e.text,
        in_reply_to: vorbelegt.in_reply_to ?? null,
        auftrag_id: vorbelegt.auftrag_id ?? null,
      }));
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Mail schreiben" breite={760} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />

      {fertig ? (
        <>
          <p className={'hinweis' + (fertig.ausgang === 'gescheitert'
                                      ? ' warnung' : '')}>
            <b>
              {fertig.ausgang === 'zugestellt' ? 'Die Mail ist hinausgegangen.'
                : fertig.ausgang === 'umgeleitet'
                  ? 'Der Testmodus läuft: die Mail ging an die Testadresse.'
                  : 'Die Mail ist NICHT hinausgegangen.'}
            </b>
            {fertig.grund ? <> {fertig.grund}</> : null}
          </p>
          <p className="klein">
            {fertig.abschrift?.ok
              ? 'Eine Abschrift liegt im Ordner „Gesendet" Ihres Postfachs.'
              : `Keine Abschrift im Postfach: ${fertig.abschrift?.grund
                  ?? 'Grund unbekannt.'}`}
          </p>
          <button className="knopf haupt" onClick={zu}>Fertig</button>
        </>
      ) : (
        <>
          {/* DIE VORLAGEN, DIE ES SCHON GIBT - und kein Satz von hier. */}
          {vorlagen && vorlagen.length > 0 && (
            <div className="reihe" style={{ marginBottom: 8 }}>
              <span className="klein leise">Vorlage:</span>
              {vorlagen.map((v) => (
                <button key={v.wert} className="knopf" disabled={!v.gefuellt}
                        title={v.gefuellt
                          ? `Betreff und Text aus „${v.schluessel.join('" und „')}"`
                          : 'Diese Vorlage ist noch nicht gepflegt '
                            + `(${v.schluessel.join(', ')}).`}
                        onClick={() => setE({
                          ...e, betreff: v.betreff, text: v.text })}>
                  {v.name}
                </button>
              ))}
            </div>
          )}
          <Feldreihe>
            <Text name="an" titel="An" wert={e.an}
                  setze={(w: string) => setE({ ...e, an: w })}
                  hinweis="Mehrere Adressen mit Komma trennen." />
            <Text name="betreff" titel="Betreff" wert={e.betreff}
                  setze={(w: string) => setE({ ...e, betreff: w })} />
            <Text name="text" titel="Text" wert={e.text} mehrzeilig
                  setze={(w: string) => setE({ ...e, text: w })} />
          </Feldreihe>
          <p className="klein leise">
            Versendet wird über n8n - dort liegen Signatur und Verteiler.
            {gesendetOrdner
              ? ' Eine Abschrift kommt danach in den Ordner „Gesendet".'
              : ' Dieses Postfach hat keinen Ordner „Gesendet"; dann gibt '
                + 'es keine Abschrift.'}
          </p>
          <button className="knopf haupt" disabled={laeuft}
                  onClick={senden}>
            {laeuft ? 'Geht hinaus …' : 'Senden'}
          </button>
        </>
      )}
    </Dialog>
  );
}

/* ========================================================================= *
 * Die Postfächer verwalten
 * ========================================================================= */

function Postfaecher({ ich, zu }: { ich: Anmelder; zu: () => void }) {
  const [konten, setKonten] = useState<any[] | null>(null);
  const [e, setE] = useState<any | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [befund, setBefund] = useState<Record<string, any>>({});
  const darfKonten = darf(ich, 'postfach.konten');

  async function laden() {
    try { setKonten((await api.mailkonten()).konten ?? []); setFehler(null); }
    catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  async function speichern() {
    setFehler(null);
    try {
      if (e.id) await api.mailkontoAendern(e.id, e);
      else await api.mailkontoAnlegen(e);
      setE(null); await laden();
    } catch (f) { setFehler(ausFehler(f)); }
  }

  async function pruefen(id: string) {
    setBefund({ ...befund, [id]: { laeuft: true } });
    try { setBefund({ ...befund, [id]: await api.mailkontoPruefen(id) }); }
    catch (f: any) {
      setBefund({ ...befund, [id]: { traegt: false, grund: f?.message } });
    }
  }

  return (
    <Dialog titel="Postfächer" breite={1040} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise">
        Ein Postfach wird hier nur EINGEHÄNGT - geholt wird nichts. Der
        automatische Weg über n8n bleibt, wie er ist.
      </p>

      <table className="liste">
        <thead>
          <tr><th>Postfach</th><th>Server</th><th>Zuletzt geprüft</th>
              <th>Wege</th></tr>
        </thead>
        <tbody>
          {(konten ?? []).map((k) => (
            <tr key={k.id}>
              <td>
                <b>{k.name}</b>
                {!k.aktiv && <span className="merker klein"> stillgelegt</span>}
                {k.vertraulich && <span className="merker warnung klein" title={"Nur mit „Abrechnung: Geld“ sichtbar"}> vertraulich</span>}
                {k.nur_ablaeufe && <span className="merker klein" title="Liest nur die Plattform, niemand blättert darin"> nur für Abläufe{k.nur_ordner ? `: ${k.nur_ordner}` : ''}</span>}
                <br /><span className="klein leise">{k.benutzer}</span>
              </td>
              <td className="klein">
                <Bruchtext text={`${k.host}:${k.port}`} />
                {k.hat_zertifikat && <><br />eigenes Zertifikat</>}
              </td>
              <td className="klein">
                {befund[k.id]?.laeuft ? 'wird geprüft …'
                  : befund[k.id]
                    ? <b className={befund[k.id].traegt ? '' : 'warnung'}>
                        {befund[k.id].traegt ? 'trägt' : 'trägt nicht'}
                        {': '}{befund[k.id].grund}
                      </b>
                    : (k.geprueft_grund
                        ? <>{k.geprueft_grund}</>
                        : <span className="leise">noch nie geprüft</span>)}
              </td>
              {/* EINE ZEILE, DIE UMBRICHT, KEIN RASTER (.240): im Raster mit
                  104 Punkten je Spalte stand „Verbindung prüfen" abgeschnitten
                  da (Marvins Bild vom 28.09.2026). */}
              <td className="knopfzeile">
                <button className="knopf" onClick={() => pruefen(k.id)}>
                  Verbindung prüfen
                </button>
                {darfKonten && (
                  <button className="knopf"
                          onClick={() => setE({ ...k, passwort: '' })}>
                    ändern
                  </button>
                )}
                {darfKonten && (
                  <button className="knopf warnung"
                          onClick={async () => {
                            if (!await fragen(`„${k.name}" aushängen? Die Mails, `
                              + 'die daraus an Aufträgen hängen, bleiben '
                              + 'stehen.')) return;
                            await api.mailkontoWeg(k.id); laden();
                          }}>
                    aushängen
                  </button>
                )}
              </td>
            </tr>
          ))}
          {konten && konten.length === 0 && (
            <tr><td colSpan={4} className="klein">
              Es ist noch kein Postfach eingehängt.
            </td></tr>
          )}
        </tbody>
      </table>

      {darfKonten && !e && (
        <button className="knopf haupt"
                onClick={() => setE({ name: '', host: '', port: 993,
                                      benutzer: '', passwort: '' })}>
          Postfach eintragen
        </button>
      )}

      {e && (
        <div className="karte">
          <h3>{e.id ? 'Postfach ändern' : 'Postfach eintragen'}</h3>
          <Feldreihe>
            <Text name="name" titel="Name" wert={e.name}
                  setze={(w: string) => setE({ ...e, name: w })}
                  hinweis="Wie es im Mailfach heißt." />
            <Text name="host" titel="Server" wert={e.host}
                  setze={(w: string) => setE({ ...e, host: w })}
                  hinweis="Zum Beispiel imap.ionos.de" />
            <Text name="port" titel="Port" wert={String(e.port ?? 993)}
                  setze={(w: string) => setE({ ...e, port: Number(w) || 993 })}
                  hinweis="993 ist IMAP über TLS. Unverschlüsselt geht nicht." />
            <Text name="benutzer" titel="Benutzer" wert={e.benutzer}
                  setze={(w: string) => setE({ ...e, benutzer: w })} />
            <Text name="passwort" titel="Passwort" wert={e.passwort ?? ''}
                  setze={(w: string) => setE({ ...e, passwort: w })}
                  hinweis={e.id
                    ? 'Leer lassen heißt: unverändert. Es gibt keinen Weg, '
                      + 'der das gespeicherte zurückgibt.'
                    : 'Es wird verschlüsselt abgelegt.'} />
            <Text name="absender" titel="Absenderadresse"
                  wert={e.absender ?? ''}
                  setze={(w: string) => setE({ ...e, absender: w })}
                  hinweis={'Steht in der Abschrift im Ordner „Gesendet".'} />
            <Text name="absender_name" titel="Absendername"
                  wert={e.absender_name ?? ''}
                  setze={(w: string) => setE({ ...e, absender_name: w })} />
            <Text name="ordner_gesendet" titel={'Ordner „Gesendet"'}
                  wert={e.ordner_gesendet ?? ''}
                  setze={(w: string) => setE({ ...e, ordner_gesendet: w })}
                  hinweis="Leer = er wird an seiner Rolle erkannt." />
            <Text name="ordner_papierkorb" titel={'Ordner „Papierkorb"'}
                  wert={e.ordner_papierkorb ?? ''}
                  setze={(w: string) => setE({ ...e, ordner_papierkorb: w })}
                  hinweis="Leer = er wird an seiner Rolle erkannt." />
            <Text name="zertifikat" titel="Eigenes Wurzelzertifikat"
                  wert={e.zertifikat ?? ''} mehrzeilig
                  setze={(w: string) => setE({ ...e, zertifikat: w })}
                  hinweis="Nur nötig, wenn der Server ein eigenes benutzt. Es wird HINZUGEFÜGT; geprüft wird weiterhin." />
          </Feldreihe>
          {/* VERTRAULICH (.240). Marvin: „nur darf die nicht für andere
              mitarbeiter sichtbar sein, weil dort sensible daten rein
              kommen." Den Haken sieht und setzt nur, wer „Abrechnung: Geld"
              hat - wer ihn setzen könnte, ohne das Geld zu sehen, legte ein
              Postfach an, das er danach selbst nicht mehr findet. */}
          {/* NUR FÜR ABLÄUFE (.489). Marvin, 26.10.2026: „ich will ungern mein Marvin-Ad
              einbinden, komplett, wo jeder was sehen kann ... Das kann nur verarbeitet
              werden, so wie wir das hier brauchen." */}
          {darf(ich, 'abrechnung.geld' as any) && (
            <>
              <label className="haken" style={{ margin: '4px 0 6px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={!!e.nur_ablaeufe}
                       onChange={(x) => setE({ ...e, nur_ablaeufe: x.target.checked })} />
                <span>
                  <b>Nur für Abläufe (unsichtbar)</b>
                  <span className="klein leise" style={{ display: 'block' }}>
                    Niemand blättert in diesem Postfach, es erscheint weder im Postfach noch unter „Ordner am Server“,
                    und keine Mail daraus wird in die Vorgänge übernommen. Nur die Plattform liest daraus,
                    z. B. die Zahlungsavise. Ist zugleich vertraulich.
                  </span>
                </span>
              </label>
              {e.nur_ablaeufe && (
                <Feldreihe>
                  <Text name="nur_ordner" titel="Nur diese Ordner" wert={e.nur_ordner ?? ''}
                        setze={(w: string) => setE({ ...e, nur_ordner: w })}
                        hinweis={'Ordnername wie in Outlook, z. B. „Zahlungsavise“; mehrere mit Semikolon. Leer: alle Ordner. „Verbindung prüfen“ zeigt die Namen.'} />
                </Feldreihe>
              )}
            </>
          )}
          {darf(ich, 'abrechnung.geld' as any) && (
            <label className="haken" style={{ margin: '4px 0 10px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={!!e.vertraulich}
                     onChange={(x) => setE({ ...e, vertraulich: x.target.checked })} />
              <span>
                <b>Vertraulich</b>
                <span className="klein leise" style={{ display: 'block' }}>
                  Dieses Postfach und alle Mails daraus sieht nur, wer „Abrechnung: Geld" hat
                  (im Postfach, im Faden und in der Akte). Für alle anderen gibt es sie nicht.
                </span>
              </span>
            </label>
          )}
          <div className="reihe">
            <button className="knopf haupt" onClick={speichern}>
              Speichern
            </button>
            <button className="knopf" onClick={() => setE(null)}>
              Abbrechen
            </button>
            {e.id && (
              <button className="knopf"
                      onClick={() => setE({ ...e, aktiv: !e.aktiv })}>
                {e.aktiv ? 'stilllegen' : 'wieder einschalten'}
              </button>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
